/**
 * Conversion between AI-generated raw rules and structured form format.
 */
import { ActionType, TriggerType, } from '@polybank/shared';
import { extractKeywordFromRegex, keywordToRegexPattern, } from './rule-keyword-utils';
import { getConditions, getActions } from './rule-accessors';
import { DATE_BEFORE_OPERATORS, DATE_AFTER_OPERATORS, DATE_EQUALS_OPERATORS, AMOUNT_LESS_OPERATORS, AMOUNT_LESS_EQUAL_OPERATORS, AMOUNT_GREATER_OPERATORS, AMOUNT_GREATER_EQUAL_OPERATORS, AMOUNT_EQUALS_OPERATORS, } from './rule-operator-aliases';
/**
 * Converts an AI-generated raw rule to structured format for form editing
 */
export function toStructured(rawRule) {
    const conditions = getConditions(rawRule);
    if (!conditions)
        return undefined;
    const triggers = conditions.map((condition) => {
        let triggerType = TriggerType.DESCRIPTION_CONTAINS;
        let triggerValue = condition.value.toString();
        if (condition.field === '$.description' ||
            condition.field === 'description') {
            if (condition.operator === 'matches' ||
                condition.operator === 'contains') {
                triggerType = TriggerType.DESCRIPTION_CONTAINS;
                if (condition.operator === 'matches') {
                    triggerValue = extractKeywordFromRegex(triggerValue);
                }
            }
            else if (condition.operator === 'not_contains' ||
                condition.operator === 'not-contains' ||
                condition.operator === 'notContains' ||
                condition.operator === 'not-matches' ||
                condition.operator === 'not_matches') {
                triggerType = TriggerType.DESCRIPTION_NOT_CONTAINS;
                if (condition.operator === 'not-matches' ||
                    condition.operator === 'not_matches') {
                    triggerValue = extractKeywordFromRegex(triggerValue);
                }
            }
            else if (condition.operator === 'starts' ||
                condition.operator === 'starts_with' ||
                condition.operator === 'starts-with' ||
                condition.operator === 'startsWith') {
                triggerType = TriggerType.DESCRIPTION_STARTS;
            }
            else if (condition.operator === 'ends' ||
                condition.operator === 'ends_with' ||
                condition.operator === 'ends-with' ||
                condition.operator === 'endsWith') {
                triggerType = TriggerType.DESCRIPTION_ENDS;
            }
        }
        else if (condition.field === '$.absoluteAmount' ||
            condition.field === 'amount') {
            if (AMOUNT_LESS_OPERATORS.has(condition.operator)) {
                triggerType = TriggerType.AMOUNT_LESS;
            }
            else if (AMOUNT_LESS_EQUAL_OPERATORS.has(condition.operator)) {
                triggerType = TriggerType.AMOUNT_LESS_EQUAL;
            }
            else if (AMOUNT_GREATER_OPERATORS.has(condition.operator)) {
                triggerType = TriggerType.AMOUNT_GREATER;
            }
            else if (AMOUNT_GREATER_EQUAL_OPERATORS.has(condition.operator)) {
                triggerType = TriggerType.AMOUNT_GREATER_EQUAL;
            }
            else if (AMOUNT_EQUALS_OPERATORS.has(condition.operator)) {
                triggerType = TriggerType.AMOUNT_EQUAL;
            }
        }
        else if (condition.field === 'currency') {
            triggerType = TriggerType.CURRENCY_IS;
        }
        else if (condition.field === 'account') {
            triggerType = TriggerType.ACCOUNT_IS;
        }
        else if (condition.field === '$.transaction_date' ||
            condition.field === 'transaction_date') {
            if (DATE_BEFORE_OPERATORS.has(condition.operator)) {
                triggerType = TriggerType.DATE_UNTIL;
            }
            else if (DATE_AFTER_OPERATORS.has(condition.operator)) {
                triggerType = TriggerType.DATE_STARTING_FROM;
            }
            else if (DATE_EQUALS_OPERATORS.has(condition.operator)) {
                triggerType = TriggerType.DATE_STARTING_FROM;
            }
        }
        return {
            triggerType,
            triggerValue,
        };
    });
    const actions = getActions(rawRule);
    const ruleActions = actions?.map((action) => {
        let actionType;
        const actionValue = action.actionValue.toString();
        if (action.actionType === 'set_category') {
            actionType = ActionType.SET_CATEGORY;
        }
        else if (action.actionType === 'set_note') {
            actionType = ActionType.SET_NOTE;
        }
        else if (action.actionType === 'set_account') {
            actionType = ActionType.SET_ACCOUNT;
        }
        else if (action.actionType === 'set_merchant') {
            actionType = ActionType.SET_MERCHANT;
        }
        else if (action.actionType === 'add_tags') {
            actionType = ActionType.SET_NOTE;
        }
        else {
            actionType = ActionType.SET_CATEGORY;
        }
        return {
            actionType,
            actionValue,
        };
    });
    return {
        triggers,
        actions: ruleActions,
    };
}
/**
 * Converts structured form data back to AI-generated raw rule format
 */
export function fromStructured(structured, metadata) {
    const conditions = structured.triggers.flatMap((trigger) => {
        let field = '$.description';
        let operator = 'matches';
        let value = trigger.triggerValue?.toString() ?? '';
        switch (trigger.triggerType) {
            case TriggerType.DESCRIPTION_CONTAINS:
                field = '$.description';
                operator = 'matches';
                value = keywordToRegexPattern(value.toString(), 'smart');
                break;
            case TriggerType.DESCRIPTION_NOT_CONTAINS:
                field = '$.description';
                operator = 'not-matches';
                value =
                    '.*' +
                        value
                            .toString()
                            .toLowerCase()
                            .replace(/([.*+?^=!:${}()|[\]/\\])/g, '\\$1') +
                        '.*';
                break;
            case TriggerType.DESCRIPTION_STARTS:
                field = '$.description';
                operator = 'starts-with';
                break;
            case TriggerType.DESCRIPTION_ENDS:
                field = '$.description';
                operator = 'ends-with';
                break;
            case TriggerType.AMOUNT_LESS:
                field = '$.absoluteAmount';
                operator = 'less-than';
                value = Number(value);
                break;
            case TriggerType.AMOUNT_LESS_EQUAL:
                field = '$.absoluteAmount';
                operator = 'less-than-or-equals';
                value = Number(value);
                break;
            case TriggerType.AMOUNT_GREATER:
                field = '$.absoluteAmount';
                operator = 'greater-than';
                value = Number(value);
                break;
            case TriggerType.AMOUNT_GREATER_EQUAL:
                field = '$.absoluteAmount';
                operator = 'greater-than-or-equals';
                value = Number(value);
                break;
            case TriggerType.AMOUNT_EQUAL:
                field = '$.absoluteAmount';
                operator = 'equals';
                value = Number(value);
                break;
            case TriggerType.CURRENCY_IS:
                field = 'currency';
                operator = 'equals';
                break;
            case TriggerType.ACCOUNT_IS:
                field = 'account';
                operator = 'equals';
                break;
            case TriggerType.DATE_STARTING_FROM:
                field = '$.transaction_date';
                operator = 'date-after-or-equals';
                value = new Date(value.toString()).toISOString();
                break;
            case TriggerType.DATE_UNTIL:
                field = '$.transaction_date';
                operator = 'date-before-or-equals';
                value = new Date(value.toString()).toISOString();
                break;
            case TriggerType.DATE_BETWEEN: {
                const [from, to] = value.toString().split(',');
                return [
                    {
                        field: '$.transaction_date',
                        operator: 'date-after-or-equals',
                        value: new Date(from).toISOString(),
                    },
                    {
                        field: '$.transaction_date',
                        operator: 'date-before-or-equals',
                        value: new Date(to).toISOString(),
                    },
                ];
            }
            default:
                field = '$.description';
                operator = 'matches';
                value = keywordToRegexPattern(value.toString(), 'exact');
        }
        return {
            field,
            operator,
            value,
        };
    });
    const actions = structured.actions.map((action) => {
        let actionType = 'set_category';
        switch (action.actionType) {
            case ActionType.SET_CATEGORY:
                actionType = 'set_category';
                break;
            case ActionType.SET_NOTE:
                actionType = 'set_note';
                break;
            case ActionType.SET_ACCOUNT:
                actionType = 'set_account';
                break;
            case ActionType.SET_MERCHANT:
                actionType = 'set_merchant';
                break;
            default:
                actionType = 'set_category';
        }
        return {
            actionType,
            actionValue: action.actionValue?.toString() ?? '',
            confidence: 0.9,
            isNewCategory: false,
        };
    });
    return {
        conditions: {
            and: conditions,
            result: {
                value: actions,
            },
        },
        reasoning: metadata?.reasoning ?? '',
        humanReadable: metadata?.humanReadable ?? '',
    };
}
